import { useAppDataStore } from "@/mobx/appDataStore.jsx";
import { DataTable } from "components/dataTable/index.jsx";
import { useCallback, useMemo, useState } from "react";
import { observer } from "mobx-react";
import moment from "moment";
import formatters from "helpers/formatters.jsx";

export default observer(function ConsignmentReports(props) {
  const appDataStore = useAppDataStore();
  const [activeTab, setActiveTab] = useState("consignments");
  const [statusAction, setStatusAction] = useState({ reportId: null, action: null });
  const [rejectionReason, setRejectionReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [photoUrl, setPhotoUrl] = useState(null);
  const [payCredit, setPayCredit] = useState({ reportId: null, photo: null, preview: null, reference: "" });

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "approved": return "bg-green-100 text-green-800";
      case "rejected":  return "bg-red-100 text-red-800";
      case "pending":   return "bg-yellow-100 text-yellow-800";
      case "credit":    return "bg-blue-100 text-blue-800";
      default:          return "bg-gray-100 text-gray-800";
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "approved": return "Aprobado";
      case "rejected":  return "Rechazado";
      case "pending":   return "Pendiente";
      case "credit":    return "Crédito";
      default:          return status;
    }
  };

  const handleStatusChange = async (reportId, newStatus, reason = "") => {
    try {
      setActionLoading(true);
      await appDataStore.post("/consignment-report/update-status", {
        reportId,
        status: newStatus,
        rejectionReason: reason
      });
      alert("Reporte actualizado exitosamente");
      setStatusAction({ reportId: null, action: null });
      setRejectionReason("");
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      alert("Error al actualizar reporte: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const handlePayCreditPhoto = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      setPayCredit(prev => ({ ...prev, photo: ev.target.result, preview: ev.target.result }));
    };
    reader.readAsDataURL(file);
  };

  const handleSubmitPayCredit = async () => {
    if (!payCredit.photo) return alert("Debes adjuntar la foto del comprobante");
    try {
      setActionLoading(true);
      await appDataStore.post("/consignment-report/pay-credit", {
        reportId: payCredit.reportId,
        photo: payCredit.photo,
        reference: payCredit.reference || undefined
      });
      alert("Pago registrado exitosamente");
      setPayCredit({ reportId: null, photo: null, preview: null });
      setRefreshKey(prev => prev + 1);
    } catch (err) {
      alert("Error al registrar pago: " + err.message);
    } finally {
      setActionLoading(false);
    }
  };

  const columns = useMemo(() => ([
    {
      accessorKey: 'user',
      header: 'Usuario',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const user = cell.getValue();
        return user ? `${user.name} (${user.email})` : "Usuario desconocido";
      }
    },
    {
      accessorKey: 'reference',
      header: 'Referencia',
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'balanceType',
      header: 'Tipo de Saldo',
    },
    {
      accessorKey: 'photo',
      header: 'Foto',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const photo = cell.getValue();
        return photo ? (
          <button onClick={() => setPhotoUrl(photo)} className="text-blue-600 hover:underline text-sm">
            Ver foto
          </button>
        ) : (
          <span className="text-gray-500">Sin foto</span>
        );
      }
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      opFilter: 'eq',
      cell: ({cell}) => {
        const status = cell.getValue();
        return (
          <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadgeClass(status)}`}>
            {getStatusLabel(status)}
          </span>
        );
      }
    },
    {
      accessorKey: 'paidAt',
      header: 'Pago',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const val = cell.getValue();
        return val ? moment(val).format('DD/MM/YYYY HH:mm') : <span className="text-gray-400 text-xs">Pendiente</span>;
      }
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('DD/MM/YYYY HH:mm'),
      exportFormat: (row) => moment(row.createdAt).format('DD/MM/YYYY HH:mm')
    },
    {
      id: 'actions',
      header: 'Acciones',
      enableColumnFilter: false,
      cell: ({row}) => {
        const report = row.original;
        if (report.status === "pending") {
          return (
            <div className="flex gap-2">
              <button
                onClick={() => handleStatusChange(report.id, "approved")}
                disabled={actionLoading}
                className="px-2 py-1 bg-green-600 text-white rounded text-xs hover:bg-green-700 disabled:bg-gray-400"
              >
                Aprobar
              </button>
              <button
                onClick={() => handleStatusChange(report.id, "credit")}
                disabled={actionLoading}
                className="px-2 py-1 bg-blue-600 text-white rounded text-xs hover:bg-blue-700 disabled:bg-gray-400"
              >
                Crédito
              </button>
              <button
                onClick={() => setStatusAction({ reportId: report.id, action: "reject" })}
                className="px-2 py-1 bg-red-600 text-white rounded text-xs hover:bg-red-700"
              >
                Rechazar
              </button>
            </div>
          );
        }
        if (report.status === "credit") {
          return (
            <button
              onClick={() => setPayCredit({ reportId: report.id, photo: null, preview: null, reference: "" })}
              className="px-2 py-1 bg-indigo-600 text-white rounded text-xs hover:bg-indigo-700"
            >
              Registrar pago
            </button>
          );
        }
        return (
          <span className="text-gray-500 text-xs">
            {report.status === "rejected" && report.rejectionReason
              ? `Rechazado: ${report.rejectionReason}`
              : "Sin acciones"}
          </span>
        );
      }
    }
  ]), [actionLoading]);

  const dataFetch = useCallback(async (records, currentPage, order, search, filter) => {
    return appDataStore.post(`/app/datasource/ConsignmentReport`, {
      records,
      currentPage,
      order,
      search,
      filter,
      attributes: ['id', 'photo', 'amount', 'reference', 'balanceType', 'status', 'rejectionReason', 'paidAt', 'createdAt'],
      include: [
        {
          model: 'User',
          as: 'user',
          attributes: ['id', 'name', 'email']
        }
      ],
      searchAttributes: ['reference']
    });
  }, [appDataStore]);

  const rechargeColumns = useMemo(() => ([
    {
      accessorKey: 'user',
      header: 'Distribuidor',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const user = cell.getValue();
        return user ? `${user.name} (${user.email})` : '—';
      },
      exportFormat: (row) => row.user ? `${row.user.name} (${row.user.email})` : '—'
    },
    {
      accessorKey: 'amount',
      header: 'Monto',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.amount
    },
    {
      accessorKey: 'previousBalance',
      header: 'Saldo Anterior',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.previousBalance
    },
    {
      accessorKey: 'newBalance',
      header: 'Saldo Nuevo',
      cell: ({cell}) => formatters.cash(cell.getValue()),
      exportFormat: (row) => row.newBalance
    },
    {
      accessorKey: 'reason',
      header: 'Motivo',
      cell: ({cell}) => <span className="text-xs text-gray-700">{cell.getValue() || '—'}</span>
    },
    {
      accessorKey: 'admin',
      header: 'Realizado por',
      enableColumnFilter: false,
      cell: ({cell}) => {
        const admin = cell.getValue();
        return admin ? `${admin.name} (${admin.email})` : '—';
      },
      exportFormat: (row) => row.admin ? `${row.admin.name} (${row.admin.email})` : '—'
    },
    {
      accessorKey: 'createdAt',
      header: 'Fecha',
      cell: ({cell}) => moment(cell.getValue()).format('DD/MM/YYYY HH:mm'),
      exportFormat: (row) => moment(row.createdAt).format('DD/MM/YYYY HH:mm')
    }
  ]), []);

  const rechargeFetch = useCallback(async (records, currentPage, order, search, filter) => {
    return appDataStore.post(`/app/datasource/BalanceRecharge`, {
      records,
      currentPage,
      order,
      search,
      filter,
      attributes: ['id', 'userId', 'amount', 'reason', 'previousBalance', 'newBalance', 'rechargedBy', 'createdAt'],
      include: [
        {model: 'User', as: 'user', attributes: ['id', 'name', 'email']},
        {model: 'User', as: 'admin', attributes: ['id', 'name', 'email']}
      ],
      searchAttributes: ['reason']
    });
  }, [appDataStore]);

  return (
    <>
      <div className="text-black">
        {/* Tabs */}
        <div className="flex gap-1 mb-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab("consignments")}
            className={`px-4 py-2 text-sm font-medium rounded-t border-b-2 transition-colors ${
              activeTab === "consignments"
                ? "border-blue-600 text-blue-600 bg-blue-50"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Reportes de Consignación
          </button>
          <button
            onClick={() => setActiveTab("recharges")}
            className={`px-4 py-2 text-sm font-medium rounded-t border-b-2 transition-colors ${
              activeTab === "recharges"
                ? "border-purple-600 text-purple-600 bg-purple-50"
                : "border-transparent text-gray-500 hover:text-gray-700"
            }`}
          >
            Recargas de Saldo
          </button>
        </div>

        {activeTab === "consignments" && <DataTable
          exportFileName={`reportes_consignacion_${moment().format('DDMMYY_HHmm')}`}
          columns={columns}
          dataFetch={dataFetch}
          defaultSorting={[{id: 'createdAt', desc: true}]}
          refreshDependencies={refreshKey}
        />}

        {activeTab === "recharges" && <DataTable
          exportFileName={`recargas_saldo_${moment().format('DDMMYY_HHmm')}`}
          columns={rechargeColumns}
          dataFetch={rechargeFetch}
          defaultSorting={[{id: 'createdAt', desc: true}]}
          enableSearch
          enableExport
        />}

        {/* Modal foto comprobante */}
        {photoUrl && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 p-4" onClick={() => setPhotoUrl(null)}>
            <div className="relative max-w-3xl max-h-[90vh] w-full flex items-center justify-center" onClick={e => e.stopPropagation()}>
              <button onClick={() => setPhotoUrl(null)} className="absolute top-0 right-0 -mt-8 text-white text-2xl font-bold leading-none hover:text-gray-300">×</button>
              <img src={photoUrl} alt="Comprobante de consignación" className="max-w-full max-h-[85vh] rounded-lg shadow-xl object-contain" />
            </div>
          </div>
        )}

        {/* Modal rechazar */}
        {statusAction.action === "reject" && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Rechazar Reporte</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Razón del rechazo</label>
                <textarea
                  value={rejectionReason}
                  onChange={(e) => setRejectionReason(e.target.value)}
                  placeholder="Ingresa la razón del rechazo..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                  rows="4"
                />
              </div>
              <div className="flex gap-2">
                <button
                  onClick={() => handleStatusChange(statusAction.reportId, "rejected", rejectionReason)}
                  disabled={actionLoading || !rejectionReason}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 disabled:bg-gray-400"
                >
                  {actionLoading ? "Procesando..." : "Confirmar Rechazo"}
                </button>
                <button
                  onClick={() => { setStatusAction({ reportId: null, action: null }); setRejectionReason(""); }}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Modal registrar pago de crédito */}
        {payCredit.reportId && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Registrar Pago de Crédito</h3>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Referencia de pago</label>
                <input
                  type="text"
                  value={payCredit.reference}
                  onChange={(e) => setPayCredit(prev => ({ ...prev, reference: e.target.value }))}
                  placeholder="Número de referencia o transacción..."
                  className="w-full px-3 py-2 border border-gray-300 rounded text-sm"
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Foto del comprobante</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={handlePayCreditPhoto}
                  className="w-full text-sm text-gray-600 file:mr-3 file:py-1 file:px-3 file:rounded file:border-0 file:bg-indigo-50 file:text-indigo-700 hover:file:bg-indigo-100"
                />
                {payCredit.preview && (
                  <img src={payCredit.preview} alt="Vista previa" className="mt-3 max-h-48 rounded border object-contain w-full" />
                )}
              </div>
              <div className="flex gap-2">
                <button
                  onClick={handleSubmitPayCredit}
                  disabled={actionLoading || !payCredit.photo}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {actionLoading ? "Procesando..." : "Confirmar Pago"}
                </button>
                <button
                  onClick={() => setPayCredit({ reportId: null, photo: null, preview: null, reference: "" })}
                  className="flex-1 px-4 py-2 bg-gray-300 text-gray-800 rounded hover:bg-gray-400"
                >
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
});